import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import ComchecksStepOne from "./ComchecksStepOne/ComchecksStepOne";
import ComchecksStepTwo from "./ComchecksStepTwo/ComchecksStepTwo";
import ComchecksStepThree from "./ComchecksStepThree/ComchecksStepThree";
import ComchecksStepFour from "./ComchecksStepFour/ComchecksStepFour";
import PrimarySelect from "../../../../Inputs/PrimarySelect/PrimarySelect";
import useCarrierCharges from "../../../../../hooks/useCarrierCharges/useCarrierCharges";
import { CarrierService } from "../../../../../services/CarrierService";
import { AlertContext } from "../../../../Dashboard/Dashboard";

export default function Comchecks({
  activeLoad,
  activeUser,
  carrierExtraPay
}) {
  const { instance } = useMsal();
  const [carrier, setCarrier] = useState({});
  const { carrierFuelTotalByCarrier, carrierComcheckAccessorials } = useCarrierCharges(carrierExtraPay, carrier);
  const [step, setStep] = useState(1);
  const [stops, setStops] = useState([]);
  const [selectedStopId, setSelectedStopId] = useState("");
  const [stagedComchecksArray, setStagedComchecksArray] = useState([]);
  const [comcheckToEdit, setComcheckToEdit] = useState({});
  const [comcheckToReview, setComcheckToReview] = useState({});
  const [issuedComchecks, setIssuedComchecks] = useState([]);
  const [parsedAccessorials, setParsedAccessorials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { warning } = useContext(AlertContext);

  const handleClear = () => {
    setComcheckToEdit({});
    setComcheckToReview({});
    setStagedComchecksArray([]);
    setSelectedStopId("");
    setStops([]);
    setStep(1);
  };

  const handleCancel = () => {
    handleClear();
    window.close();
  };

  const handleDeleteStagedComcheck = (comcheckToDelete) => {
    const comchecks = [...stagedComchecksArray];
    setStagedComchecksArray(comchecks.filter(c => c.tempId !== comcheckToDelete.tempId));
  };

  const getIssuedComchecks = () => {
    setIsLoading(true);

    CarrierService.GetIssuedComchecks(instance, carrier.id, activeLoad.id).then(res => {
      setIssuedComchecks(res);
      setIsLoading(false);
    }).catch(error => {
      console.error(error);
      warning(error);
    });
  };
 
  useEffect(() => {
    if (activeLoad?.stops) {
      activeLoad.stops.forEach(s => {
        s.name = `Stop ${s.orderSequence}: ${s.cityName}, ${s.state} (${s.stopType})`;
      });

      setStops(activeLoad.stops);
      setSelectedStopId(activeLoad.stops[0].id);
    }
  }, [activeLoad]);

  useEffect(() => {
    const accessorials = [...carrierComcheckAccessorials];

    accessorials.forEach(a => {
      const issuedMatch = issuedComchecks.find(ic => ic.baseLineItemId && ic.baseLineItemId.trim() === a.id);
      if (issuedMatch) {
        a.express = issuedMatch.express;
        a.comcheckIssued = true;
      }

      const stagedMatch = stagedComchecksArray.find(sc => sc.accessorialId === a.id);
      a.stagedComcheck = !!stagedMatch;
    }
    );

    setParsedAccessorials(accessorials);
  }, [issuedComchecks, stagedComchecksArray, carrierComcheckAccessorials]);

  return (
    <Box sx={{ maxHeight: "80vh", overflow: "auto", paddingRight: "40px" }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box>
          {step === 1 || step === 2 ? (
            <PrimarySelect 
              id="stop"
              options={stops}
              onChange={setSelectedStopId}
              variant="standard"
              value={selectedStopId}
              nameKey="name"
              valueKey="id"
              disabled
            />
          ) : (
            <Typography variant="h2" color="primary.60">
              All Stops
            </Typography>
          )}
        </Box>
        <Box sx={{ maxWidth: "800px", marginLeft: "480px" }}>
          <Typography variant="subhead1" color="black_solid.60">
          LOAD# {activeLoad.id}
          </Typography>
        </Box>
      </Box>
      {step === 1 && 
        <ComchecksStepOne
          activeLoad={activeLoad} 
          activeUser={activeUser}
          carrier={carrier} 
          setCarrier={setCarrier}
          totalFsc={carrierFuelTotalByCarrier}
          accessorials={parsedAccessorials}
          setStep={setStep}
          stagedComchecksArray={stagedComchecksArray}
          setStagedComchecksArray={setStagedComchecksArray}
          comcheckToEdit={comcheckToEdit}
          setComcheckToEdit={setComcheckToEdit}
          setComcheckToReview={setComcheckToReview}
          step={step}
          handleCancel={handleCancel}
          handleDeleteStagedComcheck={handleDeleteStagedComcheck}
          issuedComchecks={issuedComchecks}
          getIssuedComchecks={getIssuedComchecks}
          isLoading={isLoading}
        />
      }
      {step === 2 && 
        <ComchecksStepTwo
          activeLoad={activeLoad}
          comcheckToReview={comcheckToReview}
          stagedComchecksArray={stagedComchecksArray}
          setStep={setStep}
          setComcheckToEdit={setComcheckToEdit}
          setComcheckToReview={setComcheckToReview}
          handleCancel={handleCancel}
          handleDeleteStagedComcheck={handleDeleteStagedComcheck}
        />
      }
      {step === 3 && 
        <ComchecksStepThree
          activeLoad={activeLoad}
          carrier={carrier}
          stagedComchecksArray={stagedComchecksArray}
          step={step}
          setStep={setStep}
          setComcheckToEdit={setComcheckToEdit}
          handleCancel={handleCancel}
          handleDeleteStagedComcheck={handleDeleteStagedComcheck}
        />
      }
      {step === 4 &&
        <ComchecksStepFour 
          activeLoad={activeLoad}
          carrier={carrier}
          stagedComchecksArray={stagedComchecksArray}
          setStagedComchecksArray={setStagedComchecksArray}
          setStep={setStep}
          setComcheckToEdit={setComcheckToEdit}
          handleCancel={handleCancel}
          handleDeleteStagedComcheck={handleDeleteStagedComcheck}
        />
      }
    </Box>
  );
}

Comchecks.propTypes = {
  activeLoad: PropTypes.object.isRequired,
  activeUser: PropTypes.object,
  carrierExtraPay: PropTypes.array
};